import {
  Flex,
  FlexItem,
  Label,
  LabelGroup,
  Popover,
  TextInput,
} from '@patternfly/react-core'
import { OutlinedTimesCircleIcon } from '@patternfly/react-icons'
import * as React from 'react'
import { useCallback, useState } from 'react'

import { displayLabelIfExists } from '@app/domain/instrument/instrument.helpers'
import type {
  IProgram,
  IProgramInteraction,
  IProgramInteractionId} from '@app/domain/program/program.interface';
import {
  ProgramInteractionTypeId,
  editConfigurationforInteractionModalTittle,
} from '@app/domain/program/program.interface'
import { InfoPopoverContent } from '@app/utils/info-popover'
import { ActionLink } from '@app/utils/styles/action-link'
import { SmallButton } from '@app/utils/styles/small-button'

import { remoteInteractionWarningPopoverContent } from './messages'

interface RemoteInteractionProps {
  program: IProgram
  programInteraction: IProgramInteraction
  onDeleteInteraction: (programIteractionId: IProgramInteractionId) => void
  onDeleteReportTemplate: (
    programIteractionId: IProgramInteractionId,
    templateId: string,
  ) => void
  onEditInteraction: (
    modalTittle: string,
    programInteractionTypeId: ProgramInteractionTypeId,
    programInteraction?: IProgramInteraction,
  ) => void
  onEditInteractionProperties: (
    interactionId: string,
    label?: string,
    daysAfterInitialInteraction?: number,
  ) => void
  onEditInteractionReportTemplates: (
    programInteraction?: IProgramInteraction,
  ) => void
}

export const RemoteInteraction = ({
  onDeleteInteraction,
  onDeleteReportTemplate,
  onEditInteraction,
  onEditInteractionProperties,
  programInteraction,
  onEditInteractionReportTemplates,
}: RemoteInteractionProps) => {
  const [label, setLabel] = useState<string>(programInteraction.label)
  const [isPopoverVisible, setPopoverVisible] = useState(false)

  const handleDeleteInteraction = useCallback(() => {
    onDeleteInteraction(programInteraction.id)
  }, [onDeleteInteraction, programInteraction])

  const handleEditInteraction = useCallback(() => {
    onEditInteraction(
      editConfigurationforInteractionModalTittle,
      ProgramInteractionTypeId.RemoteInteraction,
      programInteraction,
    )
  }, [onEditInteraction, programInteraction])

  const handleEditInteractionReportTemplates = useCallback(() => {
    onEditInteractionReportTemplates(programInteraction)
  }, [onEditInteractionReportTemplates, programInteraction])

  const handleBlur = useCallback(() => {
    onEditInteractionProperties(programInteraction.id, label)
  }, [label, onEditInteractionProperties, programInteraction])

  const showPopover = useCallback(() => setPopoverVisible(true), [])
  const hidePopover = useCallback(() => setPopoverVisible(false), [])

  return (
    <>
      {programInteraction.instruments.length > 0 ? (
        <>
          <Flex className="interaction-label-container">
            <FlexItem>
              <Popover
                aria-label="Delete Interaction Confirmation"
                isVisible={isPopoverVisible}
                shouldClose={hidePopover}
                bodyContent={
                  <InfoPopoverContent
                    title="Danger"
                    body={remoteInteractionWarningPopoverContent}
                    isDanger={true}
                    onCancel={hidePopover}
                    onConfirm={handleDeleteInteraction}
                  />
                }
              >
                <OutlinedTimesCircleIcon
                  onClick={showPopover}
                  className="link-image"
                />
              </Popover>
            </FlexItem>
            <FlexItem>
              <TextInput
                value={label ?? ''}
                isRequired
                type="text"
                id="label-remote"
                name="label-remote"
                onChange={setLabel}
                onBlur={handleBlur}
                placeholder="Label"
              />
            </FlexItem>
          </Flex>
          <Flex>
            <FlexItem className="spacer" />
            <FlexItem className="label">Instruments:</FlexItem>
            <FlexItem>
              <SmallButton variant="secondary" onClick={handleEditInteraction}>
                Edit
              </SmallButton>
            </FlexItem>
            <FlexItem>
              {programInteraction.instruments
                ?.map(displayLabelIfExists)
                .join(', ')
                .toString()}
            </FlexItem>
          </Flex>
          <Flex>
            <FlexItem className="spacer" />
            <FlexItem className="label">Report Templates:</FlexItem>
            <FlexItem>
              <SmallButton
                variant="secondary"
                onClick={handleEditInteractionReportTemplates}
              >
                Add
              </SmallButton>
            </FlexItem>
            <FlexItem>
              <LabelGroup numLabels={10}>
                {programInteraction?.pdfTemplates?.map(
                  (reportTemplate, index) => (
                    <Label
                      key={index}
                      onClose={() => {
                        onDeleteReportTemplate(
                          programInteraction.id,
                          reportTemplate.id,
                        )
                      }}
                    >
                      {reportTemplate.label ?? reportTemplate.id}
                    </Label>
                  ),
                )}
              </LabelGroup>
            </FlexItem>
          </Flex>
        </>
      ) : (
        <Flex>
          <FlexItem className="spacer" />
          <FlexItem className="label">Instruments:</FlexItem>
          <FlexItem />
          <FlexItem>
            <ActionLink onClick={handleEditInteraction}>Select</ActionLink>
          </FlexItem>
        </Flex>
      )}
    </>
  )
}
